import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Grid, Typography, Paper, Switch, Button, IconButton,
  CircularProgress, Alert, TextField, MenuItem, Snackbar, Tooltip,
  Chip, Divider, alpha, Fade, Skeleton, keyframes,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  ContentCopy as CopyIcon,
  OpenInNew as ExternalIcon,
  TravelExplore as SeoIcon,
  Facebook as FacebookIcon,
  ShoppingCart as ShoppingIcon,
  Link as LinkIcon,
  Campaign as CampaignIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  Category as CategoryIcon,
  Storefront as BrandIcon,
  Inventory2 as ProductIcon,
  Analytics as AnalyticsIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Sync as SyncIcon,
  People as PeopleIcon,
} from '@mui/icons-material';
import { Title } from 'react-admin';
import authInstance from '../services/httpClient';
import { usePermissions } from '../context/PermissionContext';
import { useAdminTheme, StatusDot } from '../admin-theme';

// ─── Types ───────────────────────────────────────────────────────────────────

interface MarketingOverview {
  seo: { products: number; categories: number; brands: number };
  channels: {
    facebookCapi: { enabled: boolean; pixelId: string | null; events: string[] };
    googleMerchantCenter: { enabled: boolean; lastSync: string | null; syncedCount: number };
    airbridge: { enabled: boolean; trackingDomain: string | null; events?: string[] };
    ga4Measurement: { enabled: boolean; measurementId: string | null; events: string[] };
    metaAudiences?: { totalAudiences: number; audiences: any[]; autoSyncOnSignup: boolean } | null;
    emailTracking?: { openTracking: boolean; clickTracking: boolean; provider: string };
    pushAnalytics?: { fcmLabelsEnabled: boolean; openTrackingEnabled: boolean; ga4Event: string };
  };
  stockWebhook?: { receivedAt: string; productCount: number; stockOutCount: number; stockInCount: number } | null;
  coverageChecks?: { total: number; active: number; comingSoon: number; notServiced: number } | null;
  generatedAt: string;
}

// ─── Animations ──────────────────────────────────────────────────────────────

const spin = keyframes`from { transform: rotate(0deg); } to { transform: rotate(360deg); }`;

const pulse = keyframes`
  0%, 100% { opacity: 1; box-shadow: 0 0 8px currentColor; }
  50% { opacity: 0.5; box-shadow: 0 0 4px currentColor; }
`;

// ─── Helpers ─────────────────────────────────────────────────────────────────

const formatDate = (iso: string | null) => {
  if (!iso) return 'Never';
  const d = new Date(iso);
  return d.toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' })
    + ' ' + d.toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit' });
};

const quickLinks = [
  { label: 'Google Search Console', url: 'https://search.google.com/search-console' },
  { label: 'Firebase Analytics', url: 'https://console.firebase.google.com/project/live-deeliver-customer/analytics' },
  { label: 'Google Merchant Center', url: 'https://merchants.google.com' },
  { label: 'Meta Commerce Manager', url: 'https://business.facebook.com/commerce' },
  { label: 'Airbridge Dashboard', url: 'https://app.airbridge.io' },
  { label: 'App Store Connect', url: 'https://appstoreconnect.apple.com' },
  { label: 'Google Play Console', url: 'https://play.google.com/console' },
  { label: 'Sentry', url: 'https://deeliver.sentry.io' },
];

const UTM_MEDIUMS = ['email', 'social', 'whatsapp', 'sms', 'cpc', 'banner', 'referral'];

const SEGMENT_TYPES = [
  { value: 'all_customers', label: 'All Customers' },
  { value: 'recent_30d', label: 'Purchased Last 30 Days' },
  { value: 'high_value', label: 'High Value (>R500 avg)' },
  { value: 'inactive_60d', label: 'Inactive 60+ Days' },
];

// ─── Reusable card style with hover glow ────────────────────────────────────

const channelCardSx = (accentColor: string, themedCardStyle: Record<string, unknown>) => ({
  ...themedCardStyle,
  p: 2.5,
  height: '100%',
  borderRadius: 3,
  borderTop: `2px solid ${alpha(accentColor, 0.6)}`,
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    borderColor: alpha(accentColor, 0.4),
    boxShadow: `0 0 30px ${alpha(accentColor, 0.12)}, inset 0 1px 0 ${alpha(accentColor, 0.1)}`,
    transform: 'translateY(-1px)',
  },
});

// ─── Sub-components ──────────────────────────────────────────────────────────

const IconBadge: React.FC<{ color: string; children: React.ReactNode }> = ({ color, children }) => (
  <Box sx={{
    width: 44, height: 44, borderRadius: 2,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    bgcolor: alpha(color, 0.12),
    color,
    flexShrink: 0,
  }}>
    {children}
  </Box>
);

const StatKPI: React.FC<{
  label: string; value: string | number; color: string; icon: React.ReactNode;
}> = ({ label, value, color, icon }) => {
  const { colors } = useAdminTheme();
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
      <IconBadge color={color}>{icon}</IconBadge>
      <Box>
        <Typography variant="caption" sx={{
          color: colors.textMuted, fontWeight: 600,
          textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.65rem',
        }}>
          {label}
        </Typography>
        <Typography variant="h5" sx={{ color, fontWeight: 800, lineHeight: 1.2 }}>
          {typeof value === 'number' ? value.toLocaleString() : value}
        </Typography>
      </Box>
    </Box>
  );
};

const ChannelHeader: React.FC<{
  icon: React.ReactNode; title: string; enabled: boolean; loading: boolean;
  onToggle: (enabled: boolean) => void; color: string; subtitle: string;
}> = ({ icon, title, enabled, loading, onToggle, color, subtitle }) => {
  const { colors } = useAdminTheme();
  return (
    <Box sx={{ mb: 2.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <IconBadge color={color}>{icon}</IconBadge>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.3 }}>{title}</Typography>
            <Typography variant="caption" sx={{ color: colors.textMuted, fontSize: '0.7rem' }}>
              {subtitle}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <StatusDot sx={{
              bgcolor: enabled ? colors.success : colors.textMuted,
              boxShadow: enabled ? `0 0 8px ${colors.success}` : 'none',
              animation: enabled ? `${pulse} 2s ease-in-out infinite` : 'none',
              width: 8, height: 8,
            }} />
            <Typography variant="caption" sx={{
              color: enabled ? colors.success : colors.textMuted,
              fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase',
            }}>
              {enabled ? 'Live' : 'Off'}
            </Typography>
          </Box>
          <Switch
            checked={enabled}
            onChange={(e) => onToggle(e.target.checked)}
            disabled={loading}
            size="small"
            sx={{
              '& .MuiSwitch-switchBase.Mui-checked': { color },
              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: alpha(color, 0.4) },
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};

const InfoRow: React.FC<{ label: string; value: string | number }> = ({ label, value }) => {
  const { colors } = useAdminTheme();
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.75 }}>
      <Typography variant="body2" sx={{ color: colors.textMuted, fontSize: '0.8rem' }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ color: colors.textPrimary, fontWeight: 600, fontSize: '0.8rem', fontFamily: 'monospace' }}>
        {value || '—'}
      </Typography>
    </Box>
  );
};

const SkeletonCard: React.FC = () => {
  const { colors, cardStyle: themedCardStyle } = useAdminTheme();
  return (
    <Paper sx={{ ...themedCardStyle, p: 2.5, borderRadius: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
        <Skeleton variant="rounded" width={44} height={44} sx={{ bgcolor: colors.bgElevated }} />
        <Box sx={{ flex: 1 }}>
          <Skeleton width="60%" height={20} sx={{ bgcolor: colors.bgElevated }} />
          <Skeleton width="40%" height={14} sx={{ bgcolor: colors.bgElevated, mt: 0.5 }} />
        </Box>
      </Box>
      <Skeleton width="100%" height={40} sx={{ bgcolor: colors.bgElevated }} />
      <Skeleton width="80%" height={14} sx={{ bgcolor: colors.bgElevated, mt: 1 }} />
    </Paper>
  );
};

// ─── Main Component ──────────────────────────────────────────────────────────

const MarketingSEO: React.FC = () => {
  const { colors, cardStyle, glassHeaderStyle, elevatedInputStyle } = useAdminTheme();
  const { role, loading: permLoading } = usePermissions();
  const [data, setData] = useState<MarketingOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toggling, setToggling] = useState<string | null>(null);
  const [snack, setSnack] = useState<{ message: string; severity: 'success' | 'error' } | null>(null);

  // UTM Builder state
  const [utmSource, setUtmSource] = useState('');
  const [utmMedium, setUtmMedium] = useState('');
  const [utmCampaign, setUtmCampaign] = useState('');
  const [utmBaseUrl, setUtmBaseUrl] = useState('https://api.mobile.deeliver.co.za/p/');
  const [copied, setCopied] = useState(false);

  // Custom Audiences state
  const [audiences, setAudiences] = useState<Array<{
    id: string; name: string; description: string; segmentType: string;
    metaAudienceId: string | null; memberCount: number; lastSyncedAt: string | null;
  }>>([]);
  const [audiencesLoading, setAudiencesLoading] = useState(false);
  const [createDialog, setCreateDialog] = useState(false);
  const [newAudienceName, setNewAudienceName] = useState('');
  const [newAudienceDesc, setNewAudienceDesc] = useState('');
  const [newAudienceSegment, setNewAudienceSegment] = useState('');
  const [syncingAudience, setSyncingAudience] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await authInstance.get('/admin/marketing');
      setData(res.data);
      setError(null);
      try {
        const audRes = await authInstance.get('/admin/marketing/audiences');
        setAudiences(audRes.data.audiences || []);
      } catch { /* Audiences endpoint may not be deployed yet */ }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load marketing data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleToggle = useCallback(async (channel: string, enabled: boolean) => {
    setToggling(channel);
    try {
      await authInstance.put('/admin/marketing/channel', { channel, enabled });
      setData((prev) => prev ? {
        ...prev,
        channels: {
          ...prev.channels,
          [channel]: { ...prev.channels[channel as keyof typeof prev.channels], enabled },
        },
      } : prev);
      setSnack({ message: `${channel} ${enabled ? 'enabled' : 'disabled'}`, severity: 'success' });
    } catch {
      setSnack({ message: `Failed to update ${channel}`, severity: 'error' });
    } finally {
      setToggling(null);
    }
  }, []);


  const handleCreateAudience = async () => {
    try {
      await authInstance.post('/admin/marketing/audiences', {
        name: newAudienceName, description: newAudienceDesc, segmentType: newAudienceSegment,
      });
      setCreateDialog(false);
      setNewAudienceName(''); setNewAudienceDesc(''); setNewAudienceSegment('');
      // Refresh audiences
      const audRes = await authInstance.get('/admin/marketing/audiences');
      setAudiences(audRes.data.audiences || []);
      setSnack({ message: 'Audience created', severity: 'success' });
    } catch { setSnack({ message: 'Failed to create audience', severity: 'error' }); }
  };

  const handleSyncAudience = async (id: string) => {
    setSyncingAudience(id);
    try {
      const res = await authInstance.post(`/admin/marketing/audiences/${id}/sync`);
      const audRes = await authInstance.get('/admin/marketing/audiences');
      setAudiences(audRes.data.audiences || []);
      setSnack({ message: `Synced ${res.data.memberCount} members`, severity: 'success' });
    } catch { setSnack({ message: 'Failed to sync audience', severity: 'error' }); }
    finally { setSyncingAudience(null); }
  };

  const handleDeleteAudience = async (id: string) => {
    if (!window.confirm('Delete this audience? This will also remove it from Meta.')) return;
    try {
      await authInstance.delete(`/admin/marketing/audiences/${id}`);
      setAudiences((prev) => prev.filter((a) => a.id !== id));
      setSnack({ message: 'Audience deleted', severity: 'success' });
    } catch { setSnack({ message: 'Failed to delete audience', severity: 'error' }); }
  };

  const utmUrl = (() => {
    if (!utmSource || !utmMedium || !utmCampaign) return '';
    const params = new URLSearchParams({
      utm_source: utmSource,
      utm_medium: utmMedium,
      utm_campaign: utmCampaign,
    });
    return `${utmBaseUrl}?${params.toString()}`;
  })();

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    setSnack({ message: 'Copied to clipboard', severity: 'success' });
  };

  // Access control
  if (permLoading) return <Box sx={{ p: 4 }}><CircularProgress /></Box>;
  if (!role || !['owner', 'admin'].includes(role)) {
    return (
      <Box sx={{ p: 3 }}>
        <Title title="Marketing & SEO" />
        <Alert severity="error">Access Denied: Owner or Admin role required.</Alert>
      </Box>
    );
  }

  const activeChannels = data ? Object.values(data.channels).filter((c) => c && 'enabled' in c && c.enabled).length : 0;

  return (
    <Box sx={{ p: 3, bgcolor: colors.bgDeepest, minHeight: '100vh' }}>
      <Title title="Marketing & SEO" />

      {/* ── Header ──────────────────────────────────────────────────────── */}
      <Paper sx={{
        ...glassHeaderStyle,
        p: 3, mb: 3, borderRadius: 3,
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        border: `1px solid ${colors.border}`,
      }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 0.5 }}>
            <Typography variant="h5" sx={{ fontWeight: 800, color: colors.textPrimary, letterSpacing: '-0.02em' }}>
              Marketing & SEO
            </Typography>
            {data && (
              <Chip
                size="small"
                label={`${activeChannels}/5 channels active`}
                sx={{
                  bgcolor: alpha(activeChannels > 0 ? colors.success : colors.textMuted, 0.12),
                  color: activeChannels > 0 ? colors.success : colors.textMuted,
                  fontWeight: 700, fontSize: '0.7rem',
                }}
              />
            )}
          </Box>
          <Typography variant="body2" sx={{ color: colors.textSecondary }}>
            Channel controls, SEO health, and campaign attribution tools
          </Typography>
        </Box>
        <Tooltip title="Refresh data">
          <span>
            <IconButton
              onClick={fetchData} disabled={loading}
              sx={{
                color: colors.info,
                bgcolor: alpha(colors.info, 0.08),
                '&:hover': { bgcolor: alpha(colors.info, 0.15) },
                ...(loading && { animation: `${spin} 1s linear infinite` }),
              }}
            >
              <RefreshIcon />
            </IconButton>
          </span>
        </Tooltip>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}

      {loading && !data ? (
        /* Skeleton loading grid */
        <Grid container spacing={2.5}>
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <Grid size={{ xs: 12, md: 6 }} key={i}><SkeletonCard /></Grid>
          ))}
        </Grid>
      ) : data && (
        <>
          {/* ── SEO Health KPIs ────────────────────────────────────────── */}
          <Fade in timeout={400}>
            <Paper sx={{
              ...cardStyle, p: 3, mb: 2.5, borderRadius: 3,
              borderTop: `2px solid ${alpha(colors.success, 0.6)}`,
              transition: 'all 0.3s',
              '&:hover': {
                boxShadow: `0 0 30px ${alpha(colors.success, 0.08)}`,
              },
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <IconBadge color={colors.success}><SeoIcon /></IconBadge>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>SEO Health</Typography>
                    <Typography variant="caption" sx={{ color: colors.textMuted, fontSize: '0.7rem' }}>
                      Sitemap, OG tags, JSON-LD, canonical URLs, robots.txt
                    </Typography>
                  </Box>
                </Box>
                <Chip size="small" label="Always On" icon={<CheckIcon sx={{ fontSize: '14px !important' }} />} sx={{
                  bgcolor: alpha(colors.success, 0.12),
                  color: colors.success,
                  fontWeight: 700, fontSize: '0.65rem',
                  '& .MuiChip-icon': { color: 'inherit' },
                }} />
              </Box>
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <StatKPI label="Indexed Products" value={data.seo.products} color={colors.success} icon={<ProductIcon />} />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <StatKPI label="Categories" value={data.seo.categories} color={colors.info} icon={<CategoryIcon />} />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <StatKPI label="Brands" value={data.seo.brands} color={colors.purple} icon={<BrandIcon />} />
                </Grid>
              </Grid>
              <Box sx={{ mt: 2.5, display: 'flex', gap: 1 }}>
                <Button size="small" variant="outlined" href="/sitemap.xml" target="_blank" startIcon={<ExternalIcon />}
                  sx={{
                    borderColor: colors.border, color: colors.textSecondary,
                    textTransform: 'none', fontSize: '0.75rem', borderRadius: 2,
                    '&:hover': { borderColor: colors.success, color: colors.success },
                  }}>
                  Sitemap
                </Button>
                <Button size="small" variant="outlined" href="/robots.txt" target="_blank" startIcon={<ExternalIcon />}
                  sx={{
                    borderColor: colors.border, color: colors.textSecondary,
                    textTransform: 'none', fontSize: '0.75rem', borderRadius: 2,
                    '&:hover': { borderColor: colors.success, color: colors.success },
                  }}>
                  Robots.txt
                </Button>
              </Box>
            </Paper>
          </Fade>

          {/* ── Channel Cards ──────────────────────────────────────────── */}
          <Grid container spacing={2.5} sx={{ mb: 2.5 }}>
            {/* Facebook CAPI */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Fade in timeout={500} style={{ transitionDelay: '100ms' }}>
                <Paper sx={channelCardSx('#1877F2', cardStyle)}>
                  <ChannelHeader
                    icon={<FacebookIcon />} title="Facebook CAPI" color="#1877F2"
                    subtitle="Server-side conversion tracking"
                    enabled={data.channels.facebookCapi.enabled}
                    loading={toggling === 'facebookCapi'}
                    onToggle={(v) => handleToggle('facebookCapi', v)}
                  />
                  <Box sx={{ bgcolor: colors.bgElevated, borderRadius: 2, p: 1.5, mb: 1.5 }}>
                    <InfoRow label="Pixel ID" value={data.channels.facebookCapi.pixelId || 'Not configured'} />
                    <Divider sx={{ borderColor: colors.border, my: 0.5 }} />
                    <InfoRow label="Events" value={data.channels.facebookCapi.events.join(', ')} />
                  </Box>
                  <Typography variant="caption" sx={{ color: colors.textMuted, fontSize: '0.7rem' }}>
                    Server-side purchase events sent to Meta on every paid order
                  </Typography>
                  <Box sx={{ mt: 1.5 }}>
                    <Button size="small" variant="outlined" href="https://business.facebook.com/events_manager" target="_blank" startIcon={<ExternalIcon />}
                      sx={{ borderColor: colors.border, color: colors.textSecondary, textTransform: 'none', fontSize: '0.75rem', borderRadius: 2, '&:hover': { borderColor: '#1877F2', color: '#1877F2' } }}>
                      Events Manager
                    </Button>
                  </Box>
                </Paper>
              </Fade>
            </Grid>

            {/* GA4 Measurement Protocol */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Fade in timeout={500} style={{ transitionDelay: '150ms' }}>
                <Paper sx={channelCardSx('#E37400', cardStyle)}>
                  <ChannelHeader
                    icon={<AnalyticsIcon />} title="GA4 Measurement Protocol" color="#E37400"
                    subtitle="Server-side event tracking"
                    enabled={data.channels.ga4Measurement?.enabled}
                    loading={toggling === 'ga4Measurement'}
                    onToggle={(v) => handleToggle('ga4Measurement', v)}
                  />
                  <Box sx={{ bgcolor: colors.bgElevated, borderRadius: 2, p: 1.5, mb: 1.5 }}>
                    <InfoRow label="Measurement ID" value={data.channels.ga4Measurement?.measurementId || 'Not configured'} />
                    <Divider sx={{ borderColor: colors.border, my: 0.5 }} />
                    <InfoRow label="Events" value={data.channels.ga4Measurement?.events.join(', ')} />
                  </Box>
                  <Typography variant="caption" sx={{ color: colors.textMuted, fontSize: '0.7rem' }}>
                    Server-side purchase & refund events for accurate GA4 attribution
                  </Typography>
                  <Box sx={{ mt: 1.5 }}>
                    <Button size="small" variant="outlined" href="https://analytics.google.com" target="_blank" startIcon={<ExternalIcon />}
                      sx={{ borderColor: colors.border, color: colors.textSecondary, textTransform: 'none', fontSize: '0.75rem', borderRadius: 2, '&:hover': { borderColor: '#E37400', color: '#E37400' } }}>
                      Google Analytics
                    </Button>
                  </Box>
                </Paper>
              </Fade>
            </Grid>

            {/* Google Merchant Center */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Fade in timeout={500} style={{ transitionDelay: '200ms' }}>
                <Paper sx={channelCardSx('#4285F4', cardStyle)}>
                  <ChannelHeader
                    icon={<ShoppingIcon />} title="Google Merchant Center" color="#4285F4"
                    subtitle="Free product listings on Google Shopping"
                    enabled={data.channels.googleMerchantCenter.enabled}
                    loading={toggling === 'googleMerchantCenter'}
                    onToggle={(v) => handleToggle('googleMerchantCenter', v)}
                  />
                  <Box sx={{ bgcolor: colors.bgElevated, borderRadius: 2, p: 1.5, mb: 1.5 }}>
                    <InfoRow label="Products Synced" value={data.channels.googleMerchantCenter.syncedCount || '—'} />
                    <Divider sx={{ borderColor: colors.border, my: 0.5 }} />
                    <InfoRow label="Last Sync" value={formatDate(data.channels.googleMerchantCenter.lastSync)} />
                  </Box>
                  <Typography variant="caption" sx={{ color: colors.textMuted, fontSize: '0.7rem' }}>
                    Products appear in Google Shopping tab and Search results
                  </Typography>
                  <Box sx={{ mt: 1.5 }}>
                    <Button size="small" variant="outlined" href="https://merchants.google.com" target="_blank" startIcon={<ExternalIcon />}
                      sx={{ borderColor: colors.border, color: colors.textSecondary, textTransform: 'none', fontSize: '0.75rem', borderRadius: 2, '&:hover': { borderColor: '#4285F4', color: '#4285F4' } }}>
                      Merchant Center
                    </Button>
                  </Box>
                </Paper>
              </Fade>
            </Grid>

            {/* Airbridge Attribution */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Fade in timeout={500} style={{ transitionDelay: '400ms' }}>
                <Paper sx={channelCardSx('#6C5CE7', cardStyle)}>
                  <ChannelHeader
                    icon={<LinkIcon />} title="Airbridge Attribution" color="#6C5CE7"
                    subtitle="Deep linking & install attribution"
                    enabled={data.channels.airbridge.enabled}
                    loading={toggling === 'airbridge'}
                    onToggle={(v) => handleToggle('airbridge', v)}
                  />
                  <Box sx={{ bgcolor: colors.bgElevated, borderRadius: 2, p: 1.5, mb: 1.5 }}>
                    <InfoRow label="Tracking Domain" value={data.channels.airbridge.trackingDomain || 'Not configured'} />
                    <Divider sx={{ borderColor: colors.border, my: 0.5 }} />
                    <InfoRow label="Features" value="Deep links, deferred deep links, share tracking" />
                  </Box>
                  <Typography variant="caption" sx={{ color: colors.textMuted, fontSize: '0.7rem' }}>
                    Replaces Firebase Dynamic Links (deprecated Aug 2025)
                  </Typography>
                  <Box sx={{ mt: 1.5 }}>
                    <Button size="small" variant="outlined" href="https://app.airbridge.io" target="_blank" startIcon={<ExternalIcon />}
                      sx={{ borderColor: colors.border, color: colors.textSecondary, textTransform: 'none', fontSize: '0.75rem', borderRadius: 2, '&:hover': { borderColor: '#6C5CE7', color: '#6C5CE7' } }}>
                      Airbridge Dashboard
                    </Button>
                  </Box>
                </Paper>
              </Fade>
            </Grid>
          </Grid>

          {/* ── Custom Audiences ─────────────────────────────────────────── */}
          <Fade in timeout={500} style={{ transitionDelay: '450ms' }}>
            <Paper sx={{
              ...cardStyle, p: 3, mb: 2.5, borderRadius: 3,
              borderTop: `2px solid ${alpha('#1877F2', 0.6)}`,
              transition: 'all 0.3s',
              '&:hover': { boxShadow: `0 0 30px ${alpha('#1877F2', 0.08)}` },
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <IconBadge color="#1877F2"><PeopleIcon /></IconBadge>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Custom Audiences</Typography>
                    <Typography variant="caption" sx={{ color: colors.textMuted, fontSize: '0.7rem' }}>
                      Upload customer segments to Meta for ad targeting
                    </Typography>
                  </Box>
                </Box>
                <Button
                  size="small" variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={() => setCreateDialog(true)}
                  sx={{
                    borderColor: colors.border, color: colors.textSecondary,
                    textTransform: 'none', fontSize: '0.75rem', borderRadius: 2,
                    '&:hover': { borderColor: '#1877F2', color: '#1877F2' },
                  }}
                >
                  Create Audience
                </Button>
              </Box>

              {audiences.length === 0 ? (
                <Typography variant="body2" sx={{ color: colors.textMuted, textAlign: 'center', py: 4 }}>
                  No custom audiences yet. Create one to start targeting customer segments on Meta.
                </Typography>
              ) : (
                <Grid container spacing={2}>
                  {audiences.map((audience) => (
                    <Grid size={{ xs: 12, md: 6 }} key={audience.id}>
                      <Box sx={{
                        bgcolor: colors.bgElevated, borderRadius: 2, p: 2,
                        border: `1px solid ${colors.border}`,
                        transition: 'all 0.2s',
                        '&:hover': { borderColor: alpha('#1877F2', 0.3) },
                      }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                          <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{audience.name}</Typography>
                            <Chip
                              size="small"
                              label={SEGMENT_TYPES.find((s) => s.value === audience.segmentType)?.label || audience.segmentType}
                              sx={{
                                mt: 0.5,
                                bgcolor: alpha('#1877F2', 0.1),
                                color: '#1877F2',
                                fontWeight: 600, fontSize: '0.65rem',
                              }}
                            />
                          </Box>
                        </Box>
                        <InfoRow label="Members" value={audience.memberCount.toLocaleString()} />
                        <Divider sx={{ borderColor: colors.border, my: 0.5 }} />
                        <InfoRow label="Last Synced" value={formatDate(audience.lastSyncedAt)} />
                        <Box sx={{ mt: 1.5, display: 'flex', gap: 1 }}>
                          <Button
                            size="small" variant="outlined"
                            onClick={() => handleSyncAudience(audience.id)}
                            disabled={syncingAudience === audience.id}
                            startIcon={syncingAudience === audience.id ? <CircularProgress size={14} /> : <SyncIcon />}
                            sx={{
                              borderColor: colors.border, color: colors.textSecondary,
                              textTransform: 'none', fontSize: '0.75rem', borderRadius: 2,
                              '&:hover': { borderColor: '#1877F2', color: '#1877F2' },
                            }}
                          >
                            {syncingAudience === audience.id ? 'Syncing...' : 'Sync Members'}
                          </Button>
                          <Button
                            size="small" variant="outlined"
                            onClick={() => handleDeleteAudience(audience.id)}
                            startIcon={<DeleteIcon />}
                            sx={{
                              borderColor: colors.border, color: colors.textSecondary,
                              textTransform: 'none', fontSize: '0.75rem', borderRadius: 2,
                              '&:hover': { borderColor: colors.error, color: colors.error },
                            }}
                          >
                            Delete
                          </Button>
                        </Box>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              )}
            </Paper>
          </Fade>

          {/* ── UTM Link Builder ───────────────────────────────────────── */}
          <Fade in timeout={500} style={{ transitionDelay: '500ms' }}>
            <Paper sx={{
              ...cardStyle, p: 3, mb: 2.5, borderRadius: 3,
              borderTop: `2px solid ${alpha(colors.warning, 0.6)}`,
              transition: 'all 0.3s',
              '&:hover': { boxShadow: `0 0 30px ${alpha(colors.warning, 0.08)}` },
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
                <IconBadge color={colors.warning}><CampaignIcon /></IconBadge>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>UTM Link Builder</Typography>
                  <Typography variant="caption" sx={{ color: colors.textMuted, fontSize: '0.7rem' }}>
                    Build trackable campaign URLs for marketing channels
                  </Typography>
                </Box>
              </Box>
              <Grid container spacing={2}>
                <Grid size={12}>
                  <TextField
                    size="small" label="Base URL" value={utmBaseUrl}
                    onChange={(e) => setUtmBaseUrl(e.target.value)} fullWidth
                    sx={elevatedInputStyle}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <TextField
                    size="small" label="Source" value={utmSource} placeholder="e.g. newsletter"
                    onChange={(e) => setUtmSource(e.target.value)} fullWidth
                    sx={elevatedInputStyle}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <TextField
                    size="small" label="Medium" value={utmMedium} select fullWidth
                    onChange={(e) => setUtmMedium(e.target.value)}
                    sx={elevatedInputStyle}
                  >
                    {UTM_MEDIUMS.map((m) => <MenuItem key={m} value={m}>{m}</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <TextField
                    size="small" label="Campaign" value={utmCampaign} placeholder="e.g. summer_sale_2026"
                    onChange={(e) => setUtmCampaign(e.target.value)} fullWidth
                    sx={elevatedInputStyle}
                  />
                </Grid>
              </Grid>
              {utmUrl && (
                <Box sx={{
                  display: 'flex', alignItems: 'center', gap: 1,
                  mt: 2, p: 2,
                  bgcolor: alpha(colors.info, 0.06),
                  borderRadius: 2,
                  border: `1px solid ${alpha(colors.info, 0.2)}`,
                  transition: 'all 0.2s',
                }}>
                  <Typography variant="body2" sx={{
                    color: colors.info, flex: 1,
                    wordBreak: 'break-all', fontSize: '0.8rem', fontFamily: 'monospace',
                  }}>
                    {utmUrl}
                  </Typography>
                  <Tooltip title={copied ? 'Copied!' : 'Copy URL'}>
                    <IconButton size="small" onClick={() => copyToClipboard(utmUrl)} sx={{
                      color: copied ? colors.success : colors.info,
                      bgcolor: alpha(copied ? colors.success : colors.info, 0.1),
                      '&:hover': { bgcolor: alpha(colors.info, 0.2) },
                    }}>
                      {copied ? <CheckIcon fontSize="small" /> : <CopyIcon fontSize="small" />}
                    </IconButton>
                  </Tooltip>
                </Box>
              )}
            </Paper>
          </Fade>

          {/* ── Live Status Cards ──────────────────────────────────────── */}
          <Fade in timeout={500} style={{ transitionDelay: '500ms' }}>
            <Grid container spacing={2} sx={{ mb: 2.5 }}>
              {/* Meta Audiences auto-sync */}
              {data?.channels?.metaAudiences && (
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Paper sx={{ ...cardStyle, p: 2, borderRadius: 2 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.6rem', fontWeight: 700, letterSpacing: 0.5 }}>Meta Audiences</Typography>
                    <Typography variant="h6" fontWeight={700}>{data.channels.metaAudiences.totalAudiences}</Typography>
                    <Chip label="Auto-sync on signup" size="small" color="success" variant="outlined" sx={{ mt: 0.5, height: 20, fontSize: '0.6rem' }} />
                  </Paper>
                </Grid>
              )}

              {/* Email Tracking */}
              {data?.channels?.emailTracking && (
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Paper sx={{ ...cardStyle, p: 2, borderRadius: 2 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.6rem', fontWeight: 700, letterSpacing: 0.5 }}>Email Tracking</Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                      <Chip label="Opens" size="small" color={data.channels.emailTracking.openTracking ? 'success' : 'default'} sx={{ height: 20, fontSize: '0.6rem' }} />
                      <Chip label="Clicks" size="small" color={data.channels.emailTracking.clickTracking ? 'success' : 'default'} sx={{ height: 20, fontSize: '0.6rem' }} />
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block', fontSize: '0.6rem' }}>{data.channels.emailTracking.provider}</Typography>
                  </Paper>
                </Grid>
              )}

              {/* Push Analytics */}
              {data?.channels?.pushAnalytics && (
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Paper sx={{ ...cardStyle, p: 2, borderRadius: 2 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.6rem', fontWeight: 700, letterSpacing: 0.5 }}>Push Analytics</Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                      <Chip label="FCM Labels" size="small" color="success" sx={{ height: 20, fontSize: '0.6rem' }} />
                      <Chip label="Open Tracking" size="small" color="success" sx={{ height: 20, fontSize: '0.6rem' }} />
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block', fontSize: '0.6rem' }}>GA4: {data.channels.pushAnalytics.ga4Event}</Typography>
                  </Paper>
                </Grid>
              )}

              {/* Last Stock Webhook */}
              {data?.stockWebhook && (
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Paper sx={{ ...cardStyle, p: 2, borderRadius: 2 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.6rem', fontWeight: 700, letterSpacing: 0.5 }}>Last Stock Webhook</Typography>
                    <Typography variant="body2" fontWeight={600}>{formatDate(data.stockWebhook.receivedAt)}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>
                      {data.stockWebhook.productCount} products | {data.stockWebhook.stockOutCount} out | {data.stockWebhook.stockInCount} in
                    </Typography>
                  </Paper>
                </Grid>
              )}

              {/* Coverage Checks */}
              {data?.coverageChecks && data.coverageChecks.total > 0 && (
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Paper sx={{ ...cardStyle, p: 2, borderRadius: 2 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.6rem', fontWeight: 700, letterSpacing: 0.5 }}>Coverage Checks</Typography>
                    <Typography variant="h6" fontWeight={700}>{data.coverageChecks.total}</Typography>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Chip label={`${data.coverageChecks.active} active`} size="small" color="success" variant="outlined" sx={{ height: 18, fontSize: '0.55rem' }} />
                      <Chip label={`${data.coverageChecks.comingSoon} soon`} size="small" color="warning" variant="outlined" sx={{ height: 18, fontSize: '0.55rem' }} />
                      <Chip label={`${data.coverageChecks.notServiced} denied`} size="small" color="error" variant="outlined" sx={{ height: 18, fontSize: '0.55rem' }} />
                    </Box>
                  </Paper>
                </Grid>
              )}
            </Grid>
          </Fade>

          {/* ── Quick Links ──────────────────────────────────────────────── */}
          <Fade in timeout={500} style={{ transitionDelay: '600ms' }}>
            <Paper sx={{ ...cardStyle, p: 2.5, borderRadius: 3 }}>
              <Typography variant="subtitle2" sx={{
                color: colors.textMuted, mb: 2,
                textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: '0.08em', fontWeight: 700,
              }}>
                Quick Links
              </Typography>
              <Grid container spacing={1}>
                {quickLinks.map((link) => (
                  <Grid size={{ xs: 6, sm: 3 }} key={link.label}>
                    <Button
                      fullWidth size="small" variant="outlined"
                      href={link.url} target="_blank" rel="noopener"
                      endIcon={<ExternalIcon sx={{ fontSize: '0.8rem !important' }} />}
                      sx={{
                        borderColor: colors.border, color: colors.textSecondary,
                        textTransform: 'none', fontSize: '0.75rem', borderRadius: 2,
                        justifyContent: 'space-between', px: 1.5, py: 1,
                        '&:hover': {
                          borderColor: colors.info, color: colors.info,
                          bgcolor: alpha(colors.info, 0.04),
                        },
                      }}
                    >
                      {link.label}
                    </Button>
                  </Grid>
                ))}
              </Grid>
            </Paper>
          </Fade>

          {/* ── Footer ───────────────────────────────────────────────────── */}
          <Typography variant="body2" sx={{ color: colors.textMuted, mt: 2, textAlign: 'right', fontSize: '0.7rem' }}>
            Last updated: {formatDate(data.generatedAt)}
          </Typography>
        </>
      )}

      {/* ── Create Audience Dialog ──────────────────────────────────────── */}
      <Dialog
        open={createDialog}
        onClose={() => setCreateDialog(false)}
        maxWidth="sm" fullWidth
        PaperProps={{
          sx: {
            bgcolor: colors.bgCard,
            border: `1px solid ${colors.border}`,
            borderRadius: 3,
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Create Custom Audience</DialogTitle>
        <DialogContent>
          <TextField
            size="small" label="Audience Name" value={newAudienceName}
            onChange={(e) => setNewAudienceName(e.target.value)}
            fullWidth sx={{ ...elevatedInputStyle, mt: 1, mb: 2 }}
            placeholder="e.g. High Value Customers"
          />
          <TextField
            size="small" label="Description" value={newAudienceDesc}
            onChange={(e) => setNewAudienceDesc(e.target.value)}
            fullWidth sx={{ ...elevatedInputStyle, mb: 2 }}
            placeholder="Optional description"
            multiline rows={2}
          />
          <TextField
            size="small" label="Segment Type" value={newAudienceSegment}
            onChange={(e) => setNewAudienceSegment(e.target.value)}
            fullWidth select sx={elevatedInputStyle}
          >
            {SEGMENT_TYPES.map((s) => (
              <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setCreateDialog(false)}
            sx={{ color: colors.textSecondary, textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreateAudience}
            variant="contained"
            disabled={!newAudienceName || !newAudienceSegment}
            sx={{
              bgcolor: '#1877F2', textTransform: 'none',
              '&:hover': { bgcolor: alpha('#1877F2', 0.85) },
            }}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!snack} autoHideDuration={3000} onClose={() => setSnack(null)}
        message={snack?.message}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        ContentProps={{
          sx: {
            bgcolor: snack?.severity === 'error' ? colors.error : colors.bgCard,
            color: colors.textPrimary,
            border: `1px solid ${snack?.severity === 'error' ? colors.error : colors.border}`,
            borderRadius: 2,
            fontWeight: 600,
          },
        }}
      />
    </Box>
  );
};

export default MarketingSEO;
